<template>
  <div>
    <div class="mb-2 lg:mb-9">
      <ButtonGroup
        id="shape-type"
        v-model="shapeType"
        :options="shapeTypeOptions"
        itemText="label"
        itemValue="value"
      />
    </div>
    <Cropper
      ref="cropper"
      class="cropper"
      @change="changeVisibleArea"
      :key="imageUrl + shapeType"
      :src="imageUrl"
      :resizeImage="{ wheel: false }"
      :stencilProps="{
        minAspectRatio: imageRatio,
        maxAspectRatio: imageRatio,
      }"
      :defaultPosition="showPosition"
      :stencilSize="setStencilSize"
    />
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue'
import { Cropper } from 'vue-advanced-cropper'

import ButtonGroup from '@/common/components/molecules/ButtonGroup.vue'
import { ShowImageCrop } from '@/models'

const DESKTOP = 'desktop'
const MOBILE = 'mobile'

export default defineComponent({
  name: 'ImageCropper',
  props: {
    imageUrl: {
      type: String,
      required: true,
    },
    crop: {
      type: Object as PropType<ShowImageCrop>,
      required: true,
    },
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    updateImageCrop: (crop: ShowImageCrop) => true,
  },
  data() {
    return {
      shapeType: DESKTOP,
    }
  },
  components: { Cropper, ButtonGroup },
  methods: {
    setStencilSize({ boundaries }) {
      const ratio = this.imageRatio
      const size = Math.min(boundaries.width, ratio * boundaries.height)

      return {
        width: size,
        height: size / ratio,
      }
    },
    changeVisibleArea({ coordinates, image }) {
      if (!this.crop) {
        return
      }
      const newCrop = { ...this.crop }
      const x =
        image.width != coordinates.width
          ? coordinates.left / (image.width - coordinates.width)
          : 0.5
      const y =
        image.height != coordinates.height
          ? coordinates.top / (image.height - coordinates.height)
          : 0.5
      if (this.shapeType == DESKTOP) {
        newCrop.desktopX = x
        newCrop.desktopY = y
      } else {
        newCrop.mobileX = x
        newCrop.mobileY = y
      }
      this.$emit('updateImageCrop', newCrop)
    },
    showPosition({ imageSize, coordinates }) {
      if (this.crop) {
        if (this.shapeType == DESKTOP) {
          return {
            left: (imageSize.width - coordinates.width) * this.crop.desktopX,
            top: (imageSize.height - coordinates.height) * this.crop.desktopY,
          }
        } else {
          return {
            left: (imageSize.width - coordinates.width) * this.crop.mobileX,
            top: (imageSize.height - coordinates.height) * this.crop.mobileY,
          }
        }
      } else {
        return { left: 0.5, top: 0.5 }
      }
    },
  },
  computed: {
    imageRatio(): number {
      return this.shapeType == DESKTOP ? 1 : 1.5
    },
    shapeTypeOptions(): any[] {
      return [
        { label: 'Desktop', value: DESKTOP },
        { label: 'Mobile (3:2)', value: MOBILE },
      ]
    },
  },
})
</script>

<style>
.vue-advanced-cropper {
  text-align: center;
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  max-height: 100%;
  max-width: 100%;
  direction: ltr;
}

.vue-advanced-cropper__stretcher {
  pointer-events: none;
  position: relative;
  max-width: 100%;
  max-height: 100%;
}

.vue-advanced-cropper__image {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  position: absolute;
  -webkit-transform-origin: center;
  transform-origin: center;
  max-width: none !important;
}

.vue-advanced-cropper__background,
.vue-advanced-cropper__foreground {
  opacity: 1;
  background: #000;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  position: absolute;
  top: 50%;
  left: 50%;
}

.vue-advanced-cropper__foreground {
  opacity: 0.5;
}

.vue-advanced-cropper__boundaries {
  opacity: 1;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  position: absolute;
  left: 50%;
  top: 50%;
}

.vue-advanced-cropper__cropper-wrapper {
  width: 100%;
  height: 100%;
}

.vue-advanced-cropper__image-wrapper {
  overflow: hidden;
  position: absolute;
  width: 100%;
  height: 100%;
}

.vue-advanced-cropper__stencil-wrapper {
  position: absolute;
}

.vue-simple-handler {
  display: block;
  background: #fff;
  height: 10px;
  width: 10px;
}

.vue-rectangle-stencil {
  position: absolute;
  height: 100%;
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.vue-rectangle-stencil__preview {
  position: absolute;
  width: 100%;
  height: 100%;
}

.vue-rectangle-stencil--movable {
  cursor: move;
}

.vue-circle-stencil {
  position: absolute;
  height: 100%;
  width: 100%;
  -webkit-box-sizing: content-box;
  box-sizing: content-box;
  cursor: move;
}

.vue-circle-stencil__preview {
  border-radius: 50%;
  position: absolute;
  width: 100%;
  height: 100%;
}

.vue-circle-stencil--movable {
  cursor: move;
}

.vue-simple-line {
  background: 0 0;
  -webkit-transition: border 0.5s;
  transition: border 0.5s;
  border-color: rgba(255, 255, 255, 0.3);
  border-width: 0;
  border-style: solid;
}

.vue-simple-line--north,
.vue-simple-line--south {
  height: 0;
  width: 100%;
}

.vue-simple-line--east,
.vue-simple-line--west {
  height: 100%;
  width: 0;
}

.vue-simple-line--east {
  border-right-width: 1px;
}

.vue-simple-line--west {
  border-left-width: 1px;
}

.vue-simple-line--south {
  border-bottom-width: 1px;
}

.vue-simple-line--north {
  border-top-width: 1px;
}

.vue-simple-line--hover {
  opacity: 1;
  border-color: #fff;
}

.vue-preview-result {
  overflow: hidden;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  position: absolute;
  height: 100%;
  width: 100%;
}

.vue-preview-result__wrapper {
  position: absolute;
}

.vue-preview-result__image {
  pointer-events: none;
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-transform-origin: center;
  transform-origin: center;
  max-width: none !important;
}

.vue-line-wrapper {
  background: 0 0;
  position: absolute;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
}

.vue-line-wrapper--north,
.vue-line-wrapper--south {
  height: 12px;
  width: 100%;
  left: 0;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
}

.vue-line-wrapper--north {
  top: 0;
  cursor: n-resize;
}

.vue-line-wrapper--south {
  top: 100%;
  cursor: s-resize;
}

.vue-line-wrapper--east,
.vue-line-wrapper--west {
  width: 12px;
  height: 100%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  top: 0;
}

.vue-line-wrapper--east {
  left: 100%;
  cursor: e-resize;
}

.vue-line-wrapper--west {
  left: 0;
  cursor: w-resize;
}

.vue-line-wrapper--disabled {
  cursor: auto;
}

.vue-handler-wrapper {
  position: absolute;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
}

.vue-handler-wrapper__draggable {
  width: 100%;
  height: 100%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
}

.vue-handler-wrapper--west-north {
  cursor: nw-resize;
}

.vue-handler-wrapper--north {
  cursor: n-resize;
}

.vue-handler-wrapper--east-north {
  cursor: ne-resize;
}

.vue-handler-wrapper--east {
  cursor: e-resize;
}

.vue-handler-wrapper--east-south {
  cursor: se-resize;
}

.vue-handler-wrapper--south {
  cursor: s-resize;
}

.vue-handler-wrapper--west-south {
  cursor: sw-resize;
}

.vue-handler-wrapper--west {
  cursor: w-resize;
}

.vue-handler-wrapper--disabled {
  cursor: auto;
}

.vue-draggable-area {
  position: relative;
}

.vue-bounding-box {
  position: relative;
  height: 100%;
  width: 100%;
}

.vue-bounding-box__handler {
  position: absolute;
}

.vue-bounding-box__handler--west-north {
  left: 0;
  top: 0;
}

.vue-bounding-box__handler--north {
  left: 50%;
  top: 0;
}

.vue-bounding-box__handler--east-north {
  left: 100%;
  top: 0;
}

.vue-bounding-box__handler--east {
  left: 100%;
  top: 50%;
}

.vue-bounding-box__handler--east-south {
  left: 100%;
  top: 100%;
}

.vue-bounding-box__handler--south {
  left: 50%;
  top: 100%;
}

.vue-bounding-box__handler--west-south {
  left: 0;
  top: 100%;
}

.vue-bounding-box__handler--west {
  left: 0;
  top: 50%;
}

.vue-preview {
  overflow: hidden;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  position: relative;
}

.vue-preview--fill {
  width: 100%;
  height: 100%;
  position: absolute;
}

.vue-preview__wrapper {
  position: absolute;
  height: 100%;
  width: 100%;
}

.vue-preview__image {
  pointer-events: none;
  position: absolute;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-transform-origin: center;
  transform-origin: center;
  max-width: none !important;
}
</style>
